/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { ChevronLeftIcon, ChevronRightIcon } from "@eds-web/react/icons";
import { css, cva, cx, RecipeVariantProps } from "@panda/css";
import { VisuallyHidden } from "@panda/jsx";
import { EmblaCarouselType } from "embla-carousel";
import { ComponentPropsWithRef, FC, useCallback, useEffect, useState } from "react";

const carouselButtons = cva({
	base: {
		display: "flex",
		gap: "eds.dimension.10",
		pointerEvents: "none",
	},
	variants: {
		position: {
			inside: {
				insetBlockStart: "50%",
				width: "min(min(var(--slide-size), 100%), 100vw)",
				marginInline: "auto",
				justifyContent: "space-between",
				padding: "eds.dimension.16",
				position: "absolute",
				transform: "translateY(-50%)",
			},
			outside: {},
		},
		size: {
			default: {
				paddingInline: "calc(2 * var(--page-inline-padding))",
				"& button": {
					height: "eds.dimension.48",
					width: "eds.dimension.48",

					"@media (min-width: 900px)": {
						height: "eds.dimension.56",
						width: "eds.dimension.56",
					},
				},
			},
			small: {
				paddingInline: "var(--page-inline-padding)",
				"& button": {
					height: "eds.dimension.40",
					width: "eds.dimension.40",
					"& svg": {
						height: "eds.dimension.20!",
						width: "eds.dimension.20!",
					},
				},
			},
		},
	},
});

type CarouselButtonsProps = NonNullable<RecipeVariantProps<typeof carouselButtons>>;

const carouselButton = css({
	alignItems: "center",
	backgroundColor: "eds.palette.transparent.light.010",
	backdropFilter: "blur(50px)",
	borderRadius: "eds.borderRadius.full",
	cursor: "pointer",
	display: "flex",
	justifyContent: "center",
	pointerEvents: "initial",
	border: "1px solid token(colors.eds.palette.transparent.light.010)",

	"&:disabled": {
		backgroundColor: "eds.fill.secondary.disabled",
		cursor: "not-allowed",
	},

	"&:not([disabled]):hover, &:not([disabled]):focus-visible": {
		backgroundColor: "eds.fill.secondary.hover",
	},
});

type UseCosmosCarouselPrevNextButtonsType = {
	prevBtnDisabled: boolean;
	nextBtnDisabled: boolean;
	scrollPrev: () => void;
	scrollNext: () => void;
};

export const useCosmosCarouselPrevNextButtons = (
	emblaApi?: EmblaCarouselType,
): UseCosmosCarouselPrevNextButtonsType => {
	const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
	const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

	const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

	const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
		setPrevBtnDisabled(!emblaApi.canScrollPrev());
		setNextBtnDisabled(!emblaApi.canScrollNext());
	}, []);

	useEffect(() => {
		if (!emblaApi) return;

		onSelect(emblaApi);
		emblaApi.on("reInit", onSelect).on("select", onSelect);
	}, [emblaApi, onSelect]);

	return { scrollPrev, scrollNext, prevBtnDisabled, nextBtnDisabled };
};

export type CosmosCarouselPrevNextButtonsProps = {
	buttonPosition?: CarouselButtonsProps["position"];
	buttonSize?: CarouselButtonsProps["size"];
} & UseCosmosCarouselPrevNextButtonsType;

export const CosmosCarouselPrevNextButtons: React.FC<CosmosCarouselPrevNextButtonsProps> = ({
	buttonPosition = "outside",
	buttonSize = "default",
	prevBtnDisabled,
	nextBtnDisabled,
	scrollPrev,
	scrollNext,
}) => {
	return (
		<div className={carouselButtons({ position: buttonPosition, size: buttonSize })}>
			<button className={cx(carouselButton)} disabled={prevBtnDisabled} onClick={scrollPrev}>
				<ChevronLeftIcon />
				<VisuallyHidden>Previous</VisuallyHidden>
			</button>
			<button className={cx(carouselButton)} disabled={nextBtnDisabled} onClick={scrollNext}>
				<ChevronRightIcon />
				<VisuallyHidden>Next</VisuallyHidden>
			</button>
		</div>
	);
};

type UseCosmosCarouselDotButtonsType = {
	selectedIndex: number;
	scrollSnaps: number[];
	onDotButtonClick: (index: number) => void;
};

export const useCosmosCarouselDotButtons = (emblaApi?: EmblaCarouselType): UseCosmosCarouselDotButtonsType => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

	const onDotButtonClick = useCallback(
		(index: number) => {
			if (!emblaApi) return;
			emblaApi.scrollTo(index);
		},
		[emblaApi],
	);

	const onInit = useCallback((emblaApi: EmblaCarouselType) => {
		setScrollSnaps(emblaApi.scrollSnapList());
	}, []);

	const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, []);

	useEffect(() => {
		if (!emblaApi) return;

		onInit(emblaApi);
		onSelect(emblaApi);
		emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
	}, [emblaApi, onInit, onSelect]);

	return {
		selectedIndex,
		scrollSnaps,
		onDotButtonClick,
	};
};

const dotButtonStyles = cva({
	base: {
		appearance: "none",
		backgroundColor: "eds.fill.contrast.default",
		touchAction: "manipulation",
		display: "inline-block",
		textDecoration: "none",
		cursor: "pointer",
		border: "0",
		padding: "0",
		margin: "0",
		width: "eds.dimension.8",
		height: "eds.dimension.8",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: "eds.dimension.40",
		transition: "width 0.5s ease, opacity 0.3s",
		opacity: "0.5",
		_hover: {
			opacity: "1",
		},
	},
	variants: {
		type: {
			selected: {
				opacity: "1",
				width: "eds.dimension.32",
			},
			secondary: {},
		},
	},
});

type CosmoSCarouselDotButtonProps = ComponentPropsWithRef<"button"> & { isSelected?: boolean };

export const CosmosCarouselDotButton: FC<CosmoSCarouselDotButtonProps> = (props) => {
	const { children, className, isSelected, ...restProps } = props;

	return (
		<button
			className={cx(dotButtonStyles({ type: isSelected ? "selected" : "secondary" }), className)}
			type="button"
			{...restProps}
		>
			{children}
		</button>
	);
};
